import ToggleGroup, {
  ToggleButton,
  ToggleButtonSize,
} from '@/components/ui/custom/dataInput/ToggleGroup';
import useListService from '@/data/hook/useListService';
import { courseLinesQueryKeys } from '@/data/services/querykeys';
import { REQUEST_STALE_TIME_IN_MS } from '@/constants';
import { useTranslation } from 'react-i18next';
import { getLineColor } from '@/utils/courseLineColors';

type CourseLineFilterValue = number | 'all';

type CourseLineFilterProps<F extends { courseLineId?: number[] }> = {
  filter?: F;
  onFilter?: (filters: Partial<F>) => void;
  size?: ToggleButtonSize;
};

export default function CourseLineFilter<
  F extends { courseLineId?: number[] },
>({ filter, onFilter, size }: CourseLineFilterProps<F>) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'projects.page',
  });

  const { results: courseLines, isLoading } = useListService({
    ...courseLinesQueryKeys.list(),
    staleTime: REQUEST_STALE_TIME_IN_MS,
  });

  const onChange = (value: CourseLineFilterValue) => {
    if (value === 'all') {
      onFilter?.({ courseLineId: [] } as unknown as Partial<F>);
      return;
    }
    onFilter?.({
      courseLineId: [value],
    } as unknown as Partial<F>);
  };

  const getButtonClassName = (lineName: string) => {
    const { color } = getLineColor(lineName);
    const colorMap: Record<string, { regular: string; active: string }> = {
      warning: {
        regular: 'border-warning text-warning enabled:hover:bg-warning-content',
        active: 'bg-warning border-warning',
      },
      primary: {
        regular: 'text-primary',
        active: '',
      },
      secondary: {
        regular:
          'border-secondary text-secondary enabled:hover:bg-secondary-content',
        active: 'bg-secondary border-secondary',
      },
      neutral: {
        regular: 'border-neutral text-neutral enabled:hover:bg-neutral-content',
        active: 'bg-neutral border-neutral',
      },
      accent: {
        regular: 'border-accent text-accent enabled:hover:bg-accent-content',
        active: 'bg-accent border-accent',
      },
      base: {
        regular: 'border-base-300 text-base-content enabled:hover:bg-base-200',
        active: 'bg-base-200 border-base-300',
      },
    };
    return colorMap[color] || colorMap.primary;
  };

  const buttons: ToggleButton<CourseLineFilterValue>[] = [
    {
      text: t('all'),
      value: 'all',
      className: {
        regular: 'border-accent text-accent enabled:hover:bg-accent-content',
        active: 'bg-accent border-accent',
      },
    },
    ...courseLines.map(line => ({
      text: line.name,
      value: line.id,
      className: getButtonClassName(line.name),
    })),
  ];

  if (isLoading) return null;

  return (
    <ToggleGroup
      buttons={buttons}
      className={{ base: 'md:justify-start justify-end', button: '' }}
      onChange={onChange}
      value={filter?.courseLineId?.[0] || 'all'}
      size={size || 'small'}
      exclusive
    />
  );
}
